/* La identidad de una unidad: un interno NO identifica un vehículo.
 *
 * ── EL DATO QUE OBLIGA A QUE ESTO EXISTA ──────────────────────────────────
 *
 * En el padrón hay 655 tractores y 673 furgones, y **430 números existen en
 * las dos tablas**: el 774 es el tractor AH989GD y también el furgón GXQ454;
 * el 106 es JYU251 y CPW768. O sea que «el 774» no alcanza para saber de qué
 * unidad se está hablando, y ya lo comprobamos: el último cruce de portería
 * del tractor 774 es del 29/07 y el del furgón 774 es del 02/06 — dos hechos
 * distintos que un solo número devolvería mezclados.
 *
 * ── LA CONVENCIÓN ES LA DEL TALLER, NO UNA NUEVA ──────────────────────────
 *
 * El furgón lleva **F adelante** (`F774`) y el tractor va **pelado** (`774`),
 * por diez años de planillas. Es la misma clave que ya usan `taller_tickets` y
 * `taller_estadias`, así que esto no inventa un vocabulario: lo escribe en un
 * solo lugar de este lado. FlotaBot tiene su gemelo en `unidad.py`.
 *
 * Escrita adentro de cada pantalla, la próxima que pida tickets de un furgón
 * lo va a buscar por «774» y le va a mostrar los del tractor — sin fallar en
 * ningún lado, que es lo caro.
 *
 * ── Y UN NÚMERO PELADO ES UN TRACTOR, SIEMPRE ─────────────────────────────
 *
 * No se sale a mirar el padrón para ver «en cuál de las dos tablas está»,
 * porque está en las dos. Cuando no se sabe de cuál se habla, se pregunta:
 * adivinar es contestar sobre el vehículo equivocado con total confianza.
 */

export const TIPOS = ["tractor", "furgon"];

/* Y en qué padrón vive cada uno, que es lo que hace falta para ir a buscar la
 * fila. Va acá y no en la pantalla: son los mismos dos nombres. */
export const COLECCION = { tractor: "tractores", furgon: "furgones" };

/* Cómo se lo nombra en pantalla. En singular y con mayúscula inicial porque va
 * como título de la ficha: «Tractor 774». */
const NOMBRE = { tractor: "Tractor", furgon: "Furgón" };

export const esTipo = (t) => TIPOS.includes(t);

/* El camino inverso: de la colección del padrón al tipo. Lo usa la ficha del
 * padrón para saber si esta fila es una unidad con ficha propia — `personas`,
 * `stock` y `proveedores` no la tienen, y no se les inventa un enlace. */
export function tipoDeColeccion(coleccion) {
  return TIPOS.find((t) => COLECCION[t] === coleccion) || null;
}

/* Sólo los dígitos. Nadie escribe el interno igual dos veces —`F 774`, `f774`,
 * `774 `— y comparando el texto crudo el mismo camión se ve como dos. */
export function numero(interno) {
  const n = String(interno ?? "").replace(/\D/g, "");
  return n || null;
}

/* QUÉ FILA ES, cuando el número solo no alcanza.
 *
 * Aldo, 29/10, ticket 559: «cuando querés consultar la patente AF 901 BT te
 * trae solamente la patente TRN 518 que corresponde al furgón 143». El playo
 * HERMANN AF901BT se dio de alta el 16/10 como `143-P` porque el 143 ya era el
 * furgón RANDON TRN518 (en el Excel de la flota es «F 143»). `numero()` lo
 * dejaba en `143`, la ficha buscaba la primera fila con ese número y abría el
 * otro furgón, sin fallar en ningún lado.
 *
 * Así que lo que viene DESPUÉS del número —una letra, con o sin guion— es
 * parte de la identidad: `143-P`, `143P` y `143 p` son la misma fila, y
 * ninguna es el `143`. La `F` de adelante sigue siendo la convención del taller
 * (`F774` es el furgón 774), no un sufijo. Un interno común queda igual que con
 * `numero()`. `null` cuando no hay número. */
export function identidad(interno) {
  const n = numero(interno);
  if (!n) return null;
  const m = String(interno)
    .toUpperCase()
    .replace(/\s+/g, "")
    .match(/^F?(\d+)-?([A-Z]+)$/);
  return m ? `${m[1]}-${m[2]}` : n;
}

/* La clave con la que esa unidad se guarda y se compara en TODAS las tablas
 * que mezclan tractores y furgones. `null` cuando no hay número: devolver `""`
 * haría que una consulta salga igual y traiga cualquier cosa.
 */
export function clave(tipo, interno) {
  const n = numero(interno);
  if (!n || !esTipo(tipo)) return null;
  return tipo === "furgon" ? `F${n}` : n;
}

/* El camino inverso, para leer una clave que ya está guardada. */
export function desdeClave(clave) {
  const c = String(clave ?? "").trim().toUpperCase();
  const n = numero(c);
  if (!n) return null;
  return { tipo: c.startsWith("F") ? "furgon" : "tractor", interno: n };
}

export function titulo(tipo, interno) {
  const n = identidad(interno);
  if (!n || !esTipo(tipo)) return "Unidad";
  return `${NOMBRE[tipo]} ${n}`;
}

/* La patente, comparada sin guiones ni espacios. Misma normalización que
 * `duplicados`: `AB 123 CD` y `ab-123-cd` son la misma chapa, y comparando el
 * texto tipeado no se encuentran nunca — que es peor que no comparar, porque
 * la pantalla dice que está todo bien. */
export function patente(dominio) {
  return String(dominio ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "") || null;
}

/* La patente como se LEE en la chapa: `AF 021 BB`, `KFT 622`. Es la cara de
 * pantalla de `patente()`, no su reemplazo — para comparar se sigue usando la
 * pelada, porque el espacio es tipografía y no dato.
 *
 * Los dos formatos que hay en la flota: el Mercosur (dos letras, tres
 * números, dos letras, desde 2016) y el viejo argentino (tres y tres). Lo que
 * no es ninguno de los dos —una chapa chilena en portería, un interno copiado
 * en el campo de la patente— va tal cual, normalizado: partirlo «a ojo»
 * inventaría una patente que no existe, y una que se ve bien armada se cree
 * sin mirar dos veces. `null` cuando no hay nada, igual que `patente()`. */
export function formatoPatente(dominio) {
  const p = patente(dominio);
  if (!p) return null;
  let m = p.match(/^([A-Z]{2})(\d{3})([A-Z]{2})$/);
  if (m) return `${m[1]} ${m[2]} ${m[3]}`;
  m = p.match(/^([A-Z]{3})(\d{3})$/);
  if (m) return `${m[1]} ${m[2]}`;
  return p;
}

/* La ruta de la ficha. Vive acá para que ningún componente arme la dirección a
 * mano: el día que cambie, cambia en un lugar. */
export function ruta(tipo, interno) {
  // Con el sufijo: `/unidad/furgon/143-P` no es el 143 (ticket 559).
  const n = identidad(interno);
  return n && esTipo(tipo) ? `/unidad/${tipo}/${n}` : null;
}
